import { CommonModule } from '@angular/common';
import { Component, OnInit, Input, ChangeDetectorRef } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { AppIcons } from 'app/modules/shared/app.icons';
import { CardModule } from 'primeng/card';
import { TabViewModule } from 'primeng/tabview';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { NoResultFoundComponent } from 'app/modules/shared/components/no-result-found/no-result-found.component';
import { AccessProviderDirective } from 'app/modules/shared/directives/access-provider.directive';
import { StorageService } from 'app/modules/shared/services/storage.service';
import { ConfirmationService, MenuItem, MessageService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { MenuModule } from 'primeng/menu';
import { ToastModule } from 'primeng/toast';
import { SkeletonModule } from 'primeng/skeleton';
import { AppSettings } from '../../../shared/app.settings';
import { StatusComponent } from '../../../shared/components/status/status.component';
import type { Country } from '../../../shared/models/country';
import type { Status } from '../../../shared/models/status';
import { CommonBindingDataService } from '../../../shared/services/common-binding-data.service';
import { EntityService } from '../../../shared/services/entity.service';
import { MetaDataService } from '../../../shared/services/meta-data.service';
import { OverviewDetailsComponent } from './overview-details/overview-details.component';
import { ActivityFeedComponent } from './activity-feed/activity-feed.component';
import { OrdersComponent } from './orders/orders.component';
import { PatientsComponent } from './patients/patients.component';
import { ProductsComponent } from './products/products.component';
import { ProtocolUsersComponent } from './protocol-users/protocol-users.component';
import { PurchaseOrdersComponent } from './purchase-orders/purchase-orders.component';
import { SitesComponent } from './sites/sites.component';
import { SponsorsComponent } from './sponsors/sponsors.component';

import * as _ from 'lodash';
import { ConfigService } from 'app/modules/shared/services/config.service';
import { Language } from 'app/modules/shared/models/language';
import {
  TrialTableData,
  TrialAttributeDefinition,
  TrialDetails,
  TrialAttributeValues,
  TrialEntityForStatus,
  TrialAttributeLabels,
  TrialOrganizedAttribute,
  TrialStatusUpdateData
} from '../../models/trials';
import { PermissionAccessService } from 'app/modules/shared/services/permission-access.service';

interface EntityCount {
  order_count: number;
  patient_count: number;
  product_count: number;
  protocol_user_count: number;
  purchase_order_count: number;
  site_count: number;
}

@Component({
  selector: 'app-trial-details',
  standalone: true,
  imports: [
    BreadcrumbModule,
    StatusComponent,
    TabViewModule,
    TranslateModule,
    MenuModule,
    ToastModule,
    OverviewDetailsComponent,
    ButtonModule,
    AccessProviderDirective,
    NoResultFoundComponent,
    CardModule,
    ProductsComponent,
    PurchaseOrdersComponent,
    OrdersComponent,
    SitesComponent,
    ProtocolUsersComponent,
    ActivityFeedComponent,
    SponsorsComponent,
    PatientsComponent,
    CommonModule,
    SkeletonModule,
    StatusComponent
  ],
  templateUrl: './trial-details.component.html',
  styleUrl: './trial-details.component.scss'
})

export class TrialDetailsComponent implements OnInit {
  @Input() tableData: TrialTableData[];
  miIcons = AppIcons;
  routePath: MenuItem[] = [];
  trialId: string;
  dataForProtocolStatus: Array<Status> = [];
  userId: string = '';
  country: Country;
  language: Language;
  activeIndex: number = 0;
  data: TrialAttributeDefinition;
  trialAttributeData: TrialOrganizedAttribute;
  attributeLabels: TrialAttributeLabels = {};
  sponsorAttributeLabels: TrialAttributeLabels = {};
  vehicleAttributeLength: number;
  attributeValues: TrialAttributeValues = {};
  imgFileId: string;
  imgFileUrl: string;
  showStatus: boolean = false;
  vehicleEntityId: string;
  currentRow: TrialTableData;
  trialDetails: TrialDetails;
  entityForStatus: TrialEntityForStatus;
  editBtnLabel: string;
  relatedData: unknown;
  defaultLoaderImg = AppSettings.DEFAULT_LOADER_IMAGE;
  entityCode: string = AppSettings.ENTITY_CODE.PROTOCOL;
  isUnassigned: boolean = false;
  isDeleted: boolean = false;
  siteId: string = '';
  breadCrumbLabel: string;
  isReviewer: boolean = false;
  entityCount: EntityCount;
  protocolStatusAttributeId: string;
  isPatientsTab: boolean = false;
  isProductsTab: boolean = false;
  isPurchaseOrdersTab: boolean = false;
  isOrdersTab: boolean = false;
  isSitesTab: boolean = false;
  isSponsorsTab: boolean = false;
  isUsersTab: boolean = false;
  isActivityFeedTab: boolean = false;
  driverList: any[] = [];
  tabParam: string = '';
  loadedTabs: Set<number> = new Set();
  tabMapping: Map<string, number> = new Map();
  isSiteUser: boolean = false;
  protocolDates: any = { startDate: null, endDate: null };
  isPatientManagmentOn: boolean = true;

  constructor(private route: ActivatedRoute,
    public cs: CommonBindingDataService,
    private cd: ChangeDetectorRef,
    private trialService: EntityService,
    private storageService: StorageService,
    public configService: ConfigService,
    private confirmationService: ConfirmationService,
    private messageService: MessageService,
    private router: Router,
    private location: Location,
    private permissionAccessService: PermissionAccessService,
    private metaDataService: MetaDataService,
  ) {



  }

  ngOnInit() {
    this.editBtnLabel = this.cs.getLabel('lbl_edit');
    this.isSiteUser = this.configService.isSiteUser();

    this.isPatientsTab = this.permissionAccessService.hasAccess('PROTOCOL_PATIENTS', 'LIST', '', '');
    this.isProductsTab = this.permissionAccessService.hasAccess('PROTOCOL_PRODUCTS', 'LIST', '', '');
    this.isPurchaseOrdersTab = this.permissionAccessService.hasAccess('PROTOCOL_PURCHASE_ORDER', 'LIST', '', '');
    this.isOrdersTab = this.permissionAccessService.hasAccess('PROTOCOL_ORDERS', 'LIST', '', '');
    this.isSitesTab = this.permissionAccessService.hasAccess('PROTOCOL_SITES', 'LIST', '', '');
    this.isSponsorsTab = this.permissionAccessService.hasAccess('PROTOCOL_SPONSORS', 'DETAILS', '', '');
    this.isUsersTab = this.permissionAccessService.hasAccess('PROTOCOL_USERS', 'LIST', '', '');
    this.isActivityFeedTab = this.permissionAccessService.hasAccess('PROTOCOL_ACTIVITY_FEED', 'LIST', '', '');

    this.buildTabMapping();

    this.route.queryParams.subscribe(param => {
      this.trialId = param?.id
      this.tabParam = param?.tab || 'overview';
      this.setBreadCrumbLabel(this.tabParam);
      this.setRoutePath();
      this.getEntityCount();
      if (this.isSiteUser) {
        this.getProtocolUsers();
      }
    });

    this.getAttributeData();

    this.trialService.entityCurrentRow.subscribe(res => {
      this.currentRow = res;
    })

    this.loadedTabs.add(this.activeIndex);

  }

  setBreadCrumbLabel(tab: string) {
    if (tab === 'overview') {
      this.breadCrumbLabel = this.cs.getLabel('lbl_overview');
      this.activeIndex = this.tabMapping.get('overview') || 0;
    } else if (tab === 'products') {
      this.breadCrumbLabel = this.cs.getLabel('lbl_products');
      this.activeIndex = this.tabMapping.get('products') || 0;
    } else if (tab === 'purchase-orders') {
      this.breadCrumbLabel = this.cs.getLabel('lbl_purchase_orders');
      this.activeIndex = this.tabMapping.get('purchase-orders') || 0;
    } else if (tab === 'orders') {
      this.breadCrumbLabel = this.cs.getLabel('protocol.lbl_orders');
      this.activeIndex = this.tabMapping.get('orders') || 0;
    } else if (tab === 'sites') {
      this.breadCrumbLabel = this.cs.getLabel('protocol.lbl_sites');
      this.activeIndex = this.tabMapping.get('sites') || 0;
    } else if (tab === 'sponsors') {
      this.breadCrumbLabel = this.cs.getLabel('protocol.lbl_sponsors');
      this.activeIndex = this.tabMapping.get('sponsors') || 0;
    } else if (tab === 'protocol-users') {
      this.breadCrumbLabel = this.cs.getLabel('protocol.lbl_protocol_users');
      this.activeIndex = this.tabMapping.get('protocol-users') || 0;
    } else if (tab === 'activity-feed') {
      this.breadCrumbLabel = this.cs.getLabel('protocol.lbl_activity_feed');
      this.activeIndex = this.tabMapping.get('activity-feed') || 0;
    } else if (tab === 'patients') {
      this.breadCrumbLabel = this.cs.getLabel('protocol.lbl_patients');
      this.activeIndex = this.tabMapping.get('patients') || 0;
    } else {
      this.breadCrumbLabel = this.cs.getLabel('lbl_overview');
      this.activeIndex = this.tabMapping.get('overview') || 0;
    }

    this.validateAndSetActiveIndex();
  }

  setRoutePath() {
    this.routePath = [
      {
        label: this.cs.getLabel('protocol.lbl_protocol'),
        routerLink: '/app/trials',
        icon: 'pi pi-arrow-left',
        iconStyle: { 'font-weight': 'bold', 'margin-right': '10px' },
        command: () => {
          this.navigateBack();
        }
      },
      {
        label: this.breadCrumbLabel,
        styleClass: 'breadcrumb-child forward-slash breadcrumb-text',
        style: { 'display': 'flex', 'top': '2px', 'position': 'relative' }
      },
    ];
  }


  activeIndexChange(event: number) {
    this.activeIndex = event;
    this.loadedTabs.add(event);
    this.storageService.setItem('protocolActiveIndex', event);

    let tabParam = 'overview';
    for (const [tabName, tabIndex] of this.tabMapping.entries()) {
      if (tabIndex === event) {
        tabParam = tabName;
        break;
      }
    }

    const url = this.router.createUrlTree(['app/trials/trial-details'], {
      queryParams: { tab: tabParam, id: this.trialId }
    });
    this.location.replaceState(url.toString());
    this.getEntityCount();
  }

  getAttributeData() {
    this.country = JSON.parse(localStorage.getItem(AppSettings.COUNTRY));
    this.language = JSON.parse(localStorage.getItem(AppSettings.LANGUAGE));
    this.trialService.getAttributeDefinition(AppSettings.ENTITY_CODE.PROTOCOL, AppSettings.VIEW_CODE.DETAIL_VIEW).subscribe((res: TrialAttributeDefinition) => {
      this.data = res;
      this.attributeLabels = this.cs.getAttributeLabels(this.data) as unknown as TrialAttributeLabels;
      this.trialAttributeData = this.cs.getOrganizedAttribute(this.data) as unknown as TrialOrganizedAttribute;
      this.protocolStatusAttributeId = res.tabs[0].groups[0].fields.find(field => field.attributeCode === 'protocol_status')?.attributeId;
      this.vehicleAttributeLength = this.trialAttributeData.tabs.length;
      const sponsor = res.relatedAttributes.find(ele => ele.entityCode === 'sponsor');
      this.sponsorAttributeLabels = this.cs.getAttributeLabels(sponsor) as unknown as TrialAttributeLabels;
      this.getTrialDetails();
      this.cd.detectChanges();
    })
  }

  getTrialDetails() {
    this.trialService.getEntity(this.trialId, AppSettings.ENTITY_CODE.PROTOCOL, AppSettings.VIEW_CODE.DETAIL_VIEW).subscribe((result: TrialDetails) => {
      this.attributeValues = this.cs.getOrgAttributeValues(result) as unknown as TrialAttributeValues;
      this.trialDetails = result;

      const sponsor = _.cloneDeep(result?.relatedData?.filter(ele => ele.entityCode === AppSettings.ENTITY_CODE.SPONSOR)[0]);
      if (sponsor) {
        this.attributeValues.sponsor_details = this.cs.getOrgAttributeValues(sponsor) as unknown as Record<string, unknown>;
        this.attributeValues.sponsor_details.entityId = this.attributeValues.sponsor_entity_id;
      }

      this.isDeleted = this.getDeleted();
      this.isUnassigned = this.getUnassigned();
      this.showStatus = true;
      this.setStatusData();
    })
  }

  setStatusData() {
    const findAttributeValue = (attributeCode: string): string | number | boolean =>
      this.trialDetails?.attributeCodeValueDtoList.find(ele => ele.attributeCode === attributeCode)?.attributeValue;

    const protocolStatus = findAttributeValue('protocol_status');
    const protocolCode = findAttributeValue('protocol_code');
    const startDateValue = findAttributeValue('start_date');
    const endDateValue = findAttributeValue('end_date');
    const startDate = startDateValue ? this.metaDataService.formatDateTimeOnTZ(startDateValue as string | number, 'MMM D, YYYY') : '';
    const endDate = endDateValue ? this.metaDataService.formatDateTimeOnTZ(endDateValue as string | number, 'MMM D, YYYY') : '';
    const protocolDate = startDate && endDate ? `${startDate} - ${endDate}` : startDate || endDate || '';
    this.protocolDates.startDate = findAttributeValue('start_date');
    this.protocolDates.endDate = findAttributeValue('end_date');
    this.entityForStatus = {
      entityName: `${findAttributeValue('protocol_name')}`,
      entityStatus: protocolStatus as unknown as Record<string, unknown>,
      otherFields: {
        'ID': protocolCode as string,
        'Date': protocolDate,
      }
    };
    this.dataForProtocolStatus = [
    ];
  }

  edit() {
    this.router.navigate([`app/trials/add-trial/${this.trialId}`]);
  }

  getDeleted(): boolean {
    return this.trialDetails?.actionStatus === 'DELETED';
  }

  getUnassigned(): boolean {
    return this.trialDetails?.actionStatus === 'UNASSIGNED';
  }

  getEntityCount() {
    this.trialService.getEntityDashboardCount(AppSettings.ENTITY_CODE.PROTOCOL, this.trialId, false).subscribe((res: EntityCount) => {
      this.entityCount = res;
    })
  }

  getProtocolUsers() {
    this.trialService.getProtocolUser(this.trialId).subscribe((res: any) => {
      if (res && res.canManagePatient !== undefined) {
        this.isPatientManagmentOn = res.canManagePatient;
      }
    });
  }

  onEntityAdded() {
    this.getTrialDetails();
    this.getEntityCount();
  }

  navigateToTab(index: number) {
    this.activeIndex = index;
    this.activeIndexChange(index);
  }

  getTabIndexByName(tabName: string): number {
    return this.tabMapping.get(tabName) || 0;
  }

  generateOrderReport() {
    this.router.navigate([`app/trial-summary-report/${this.trialId}`]);
  }

  onDeactivate() {
    this.onUpdateStatus('Inactive', this.cs.getLabel('product.lbl_deactivate'));
  }
  onActivate() {
    this.onUpdateStatus('Running', this.cs.getLabel('product.lbl_activate'));
  }


  onUpdateStatus(status: string, acceptLabelText: string) {
    const isActive = status === 'Running';

    this.confirmationService.confirm({
      header: this.cs.getLabel(isActive ? 'protocol.lbl_activate_confirmation' : 'protocol.lbl_deactivate_confirmation'),
      message: this.cs.getLabel(isActive ? 'protocol.msg_activate_confirmation' : 'protocol.msg_deactivate_confirmation'),
      acceptIcon: 'none',
      rejectIcon: 'none',
      rejectButtonStyleClass: 'bg-white text-color cancel',
      acceptButtonStyleClass: 'bg-red-500 delete',
      acceptLabel: acceptLabelText,
      rejectLabel: this.cs.getLabel('lbl_cancel'),
      accept: () => {
        const protocolData: TrialStatusUpdateData = {
          forTenantCode: this.configService.getForTenantCode(),
          entityCode: AppSettings.ENTITY_CODE.PROTOCOL,
          countryCode: this.country[0].countryCode,
          languageCode: this.language?.[0]?.langCode || 'en',
          data: [{
            attributeId: this.protocolStatusAttributeId,
            attributeValue: status
          }],
          relationshipData: [],
          viewCode: AppSettings.VIEW_CODE.PROTOCOL_STATUS_UPDATE_VIEW,
        };

        this.trialService.saveAttributeData(protocolData.entityCode, this.trialId, protocolData).subscribe({
          next: () => {
            this.messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: this.cs.getLabel('protocol.lbl_status_updated_successfully'),
            });
            setTimeout(() => {
              this.getTrialDetails();
              this.getEntityCount();
            }, 300);
          },
          error: () => {
          }
        });
      },
      reject: () => {
      }
    });
  }

  isAllowToAddProduct(): boolean {
    return this.isAllowToDeactivate();
  }

  isChangeReasonNotRequired(): boolean {
    if (!this.attributeValues.protocol_status) return false;
    const status = this.attributeValues.protocol_status;
    return Object.keys(status)[0]?.toLowerCase() === 'draft';
  }

  isEditNotAllowedSpecificStatus(): boolean {
    if (!this.attributeValues.protocol_status) return false;
    const status = this.attributeValues.protocol_status;
    return Object.keys(status)[0]?.toLowerCase() === 'running';
  }

  isEditNotAllowed(): boolean {
    if (!this.attributeValues.protocol_status) return false;
    const status = this.attributeValues.protocol_status;
    return Object.keys(status)[0]?.toLowerCase() === 'completed' || Object.keys(status)[0]?.toLowerCase() === 'inactive';
  }

  isAllowToGenerateReport(): boolean {
    return false;
    if (!this.attributeValues.protocol_status) return false;
    const status = this.attributeValues.protocol_status;
    const permission = this.permissionAccessService.hasAccess('PROTOCOLS', '', 'ALL', 'ACTIVATE');
    return Object.keys(status)[0]?.toLowerCase() !== 'draft' && permission;
  }

  isAllowToEdit(): boolean {
    if (!this.attributeValues.protocol_status) return false;
    const status = this.attributeValues.protocol_status;
    const permission = this.permissionAccessService.hasAccess('PROTOCOLS', '', 'ALL', 'EDIT');
    return (Object.keys(status)[0]?.toLowerCase() !== 'completed' && Object.keys(status)[0]?.toLowerCase() !== 'running' && Object.keys(status)[0]?.toLowerCase() !== 'inactive') && permission;
  }

  isProtocolStatusDraft(): boolean {
    if (!this.attributeValues.protocol_status) return false;
    const status = this.attributeValues.protocol_status;
    return Object.keys(status)[0]?.toLowerCase() === 'draft' || Object.keys(status)[0]?.toLowerCase() === 'inactive';
  }


  isProtocolInactiveOrCompleted(): boolean {
    if (!this.attributeValues.protocol_status) return false;
    const status = this.attributeValues.protocol_status;
    return Object.keys(status)[0]?.toLowerCase() === 'inactive' || Object.keys(status)[0]?.toLowerCase() === 'completed';
  }

  isAllowToDeactivate(): boolean {
    return !this.isProtocolInactiveOrCompleted();
    // return this.permissionAccessService.hasAccess('PROTOCOLS', 'ALL', 'DEACTIVATE', '');
  }

  isTabLoaded(tabIndex: number): boolean {
    return this.loadedTabs.has(tabIndex);
  }

  isTabLoadedByName(tabName: string): boolean {
    const tabIndex = this.tabMapping.get(tabName);
    return tabIndex !== undefined && this.loadedTabs.has(tabIndex);
  }

  isTabAccessible(tabIndex: number): boolean {
    for (const [tabName, mappedIndex] of this.tabMapping.entries()) {
      if (mappedIndex === tabIndex) {
        switch (tabName) {
          case 'overview': return true;
          case 'products': return this.isProductsTab;
          case 'purchase-orders': return this.isPurchaseOrdersTab;
          case 'orders': return this.isOrdersTab;
          case 'sites': return this.isSitesTab;
          case 'sponsors': return this.isSponsorsTab;
          case 'protocol-users': return this.isUsersTab;
          case 'activity-feed': return this.isActivityFeedTab;
          case 'patients': return this.isPatientsTab;
          default: return false;
        }
      }
    }
    return false;
  }

  getFirstAccessibleTabIndex(): number {
    for (const [, tabIndex] of this.tabMapping.entries()) {
      if (this.isTabAccessible(tabIndex)) {
        return tabIndex;
      }
    }
    return 0;
  }

  validateAndSetActiveIndex(): void {
    if (!this.isTabAccessible(this.activeIndex)) {
      this.activeIndex = this.getFirstAccessibleTabIndex();
      this.loadedTabs.add(this.activeIndex);
    }
  }

  buildTabMapping() {
    this.tabMapping.clear();
    let currentIndex = 0;

    this.tabMapping.set('overview', currentIndex++);

    if (this.isProductsTab) {
      this.tabMapping.set('products', currentIndex++);
    }

    if (this.isPurchaseOrdersTab) {
      this.tabMapping.set('purchase-orders', currentIndex++);
    }

    if (this.isOrdersTab) {
      this.tabMapping.set('orders', currentIndex++);
    }

    if (this.isSitesTab) {
      this.tabMapping.set('sites', currentIndex++);
    }

    if (this.isSponsorsTab) {
      this.tabMapping.set('sponsors', currentIndex++);
    }

    if (this.isUsersTab) {
      this.tabMapping.set('protocol-users', currentIndex++);
    }

    if (this.isActivityFeedTab) {
      this.tabMapping.set('activity-feed', currentIndex++);
    }

    if (this.isPatientsTab) {
      this.tabMapping.set('patients', currentIndex++);
    }

  }

  navigateBack() {
    this.route.queryParams.subscribe(params => {
      const fromTab = params['fromTab'];

      if (fromTab === 'site-protocols') {
        this.location.back();
      } else if (fromTab && ['all', 'draft', 'active', 'completed', 'inactive'].includes(fromTab)) {
        this.storageService.setItem('protocolActiveIndex', this.getTabIndexFromParam(fromTab));
        this.router.navigate(['app/trials']);
      } else {
        this.storageService.setItem('protocolActiveIndex', 0);
        this.router.navigate(['app/trials']);
      }
    });
  }

  private getTabIndexFromParam(tabParam: string): number {
    switch (tabParam.toLowerCase()) {
      case 'all':
        return 0;
      case 'draft':
        return 1;
      case 'active':
        return 2;
      case 'completed':
        return 3;
      case 'inactive':
        return 4;
      default:
        return 0;
    }
  }
}

